import { WebDashboardContentSpecBlocksInner } from "@coeff/agent-types";
import React, { useEffect, useMemo, useState } from "react";

import { DetailsIcon, MessageIcon, ScheduleIcon } from "../../../components";
import { getTimeZone, isValidEmail } from "../../../utils";

import {
  AddDestinationPicker,
  BlockPicker,
  DestinationBlock,
  DestinationIcon,
  MonthlyDayGrid,
  SectionHeader,
  SegmentTabs,
  WeeklyDayRow,
  SelectDropdown,
  SlackSubscriptionAuthModal,
  TeamsAuthModal,
} from "./primitives";
import {
  buildScheduleLabel,
  DashboardSubscription,
  defaultFormState,
  DESTINATION_TYPE_OPTIONS_SUBSCRIBE_MODAL,
  FREQUENCY_OPTIONS,
  getScheduleHourLabels,
  SCOPE_OPTIONS,
  SubscriptionDestinationType,
  SubscriptionFormState,
  subscriptionToFormState,
} from "./types";

// ─── Subscription form ────────────────────────────────────────────────────────

interface SubscriptionFormProps {
  portalDocument: Document;
  title: string;
  initialState?: DashboardSubscription;
  blocks: WebDashboardContentSpecBlocksInner[];
  onSave: (sub: Omit<DashboardSubscription, "id">) => void;
  onCancel: () => void;
  onClose: () => void;
  showBack?: boolean;
  isSaving?: boolean;
  onPause?: () => void;
  onDelete?: () => void;
  isPaused?: boolean;
  isSlackAuthorized?: boolean;
  isSlackAuthInProgress?: boolean;
  onStartSlackOAuth?: () => void;
  onCancelSlackOAuth?: () => void;
  isTeamsAuthorized?: boolean;
  isTeamsAuthInProgress?: boolean;
  onStartTeamsOAuth?: () => void;
  onCancelTeamsOAuth?: () => void;
  slackAuthSuccessTick?: number;
  teamsAuthSuccessTick?: number;
}

export const SubscriptionForm: React.FC<SubscriptionFormProps> = ({
  portalDocument,
  title,
  initialState,
  blocks,
  onSave,
  onCancel,
  onClose,
  showBack = false,
  isSaving = false,
  onPause,
  onDelete,
  isPaused = false,
  isSlackAuthorized,
  isSlackAuthInProgress,
  onStartSlackOAuth,
  onCancelSlackOAuth,
  isTeamsAuthorized,
  isTeamsAuthInProgress,
  onStartTeamsOAuth,
  onCancelTeamsOAuth,
  slackAuthSuccessTick = 0,
  teamsAuthSuccessTick = 0,
}) => {
  const [form, setForm] = useState<SubscriptionFormState>(
    initialState ? subscriptionToFormState(initialState) : defaultFormState()
  );
  const [authModalTarget, setAuthModalTarget] = useState<"slack" | "teams" | null>(null);
  const [formValidationError, setFormValidationError] = useState<string | null>(null);
  const [pendingAuthDestinationType, setPendingAuthDestinationType] = useState<
    "slack" | "teams" | null
  >(null);
  const [autoOpenDestinationIndex, setAutoOpenDestinationIndex] = useState<number | null>(null);

  const tzForScheduleLabels = initialState?.scheduleTzName ?? getTimeZone();
  const scheduleHourLabels = useMemo(
    () => getScheduleHourLabels(tzForScheduleLabels),
    [tzForScheduleLabels]
  );

  useEffect(() => {
    if (pendingAuthDestinationType !== "slack") {
      return;
    }
    if (slackAuthSuccessTick <= 0) {
      return;
    }

    setField("destinations", [...form.destinations, { type: "slack", recipients: [] }]);
    setAutoOpenDestinationIndex(form.destinations.length);
    setPendingAuthDestinationType(null);
    setAuthModalTarget(null);
  }, [form.destinations, pendingAuthDestinationType, slackAuthSuccessTick]);

  useEffect(() => {
    if (pendingAuthDestinationType !== "teams") {
      return;
    }
    if (teamsAuthSuccessTick <= 0) {
      return;
    }

    setField("destinations", [...form.destinations, { type: "teams", recipients: [] }]);
    setAutoOpenDestinationIndex(form.destinations.length);
    setPendingAuthDestinationType(null);
    setAuthModalTarget(null);
  }, [form.destinations, pendingAuthDestinationType, teamsAuthSuccessTick]);

  const setField = <K extends keyof SubscriptionFormState>(
    key: K,
    value: SubscriptionFormState[K]
  ) => {
    if (formValidationError) {
      setFormValidationError(null);
    }
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const invalidEmailRecipients = Array.from(
      new Set(
        form.destinations
          .filter(dest => dest.type === "email")
          .flatMap(dest => dest.recipients)
          .map(email => email.trim())
          .filter(email => !isValidEmail(email))
      )
    );

    if (invalidEmailRecipients.length > 0) {
      setFormValidationError(
        `Invalid email${
          invalidEmailRecipients.length > 1 ? "s" : ""
        }: ${invalidEmailRecipients.join(", ")}`
      );
      return;
    }

    setFormValidationError(null);
    const authorizedDestinations = form.destinations.filter(dest => {
      if (dest.type === "slack") {
        return Boolean(isSlackAuthorized);
      }
      if (dest.type === "teams") {
        return Boolean(isTeamsAuthorized);
      }
      return true;
    });

    const hasRecipient = authorizedDestinations.some(dest =>
      dest.recipients.some(r => r.trim().length > 0)
    );
    if (!hasRecipient) {
      setFormValidationError("Add at least one recipient before saving.");
      return;
    }

    onSave({
      frequency: form.frequency,
      hourIndex: form.hourIndex,
      weekdays: form.weekdays,
      daysOfMonth: form.daysOfMonth,
      scope: form.scope,
      blockIds: form.blockIds,
      includeAiSummary: form.includeAiSummary,
      destinations: authorizedDestinations,
      scheduleTzName: initialState?.scheduleTzName,
      scheduleLabel: buildScheduleLabel(form, tzForScheduleLabels),
      paused: initialState?.paused,
    });
  };

  const handleAddDestination = (type: SubscriptionDestinationType) => {
    if (type === "slack" && !isSlackAuthorized) {
      setAuthModalTarget("slack");
      setPendingAuthDestinationType("slack");
      return;
    }

    if (type === "teams" && !isTeamsAuthorized) {
      setAuthModalTarget("teams");
      setPendingAuthDestinationType("teams");
      return;
    }

    setField("destinations", [...form.destinations, { type, recipients: [] }]);
    if (type === "slack" || type === "teams") {
      setAutoOpenDestinationIndex(form.destinations.length);
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-5 pb-3">
        <div className="flex items-center gap-1.5">
          {showBack && (
            <button
              type="button"
              onClick={onCancel}
              className="p-1.5 -ml-1.5 text-main-muted-foreground hover:text-main-foreground rounded-lg hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
              aria-label="Back"
            >
              <span className="material-icons !text-xl">arrow_back</span>
            </button>
          )}
          <h2 className="text-base font-semibold text-main-foreground">{title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 text-main-muted-foreground hover:text-main-foreground rounded-lg hover:bg-main-text-button-hover-background cursor-pointer transition-colors"
          aria-label="Close"
        >
          <span className="material-icons !text-xl">close</span>
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3 px-5 overflow-y-auto max-h-[80vh] pb-1">
        {/* When to send */}
        <div className="flex flex-col gap-3 rounded bg-main-background-secondary p-2">
          <SectionHeader
            icon={<ScheduleIcon className="h-4 w-4 shrink-0" />}
            label="When to send"
          />
          <SegmentTabs
            options={FREQUENCY_OPTIONS}
            value={form.frequency}
            onChange={v => setField("frequency", v)}
            size="sm"
          />
          {form.frequency === "weekly" && (
            <WeeklyDayRow weekdays={form.weekdays} onChange={v => setField("weekdays", v)} />
          )}
          {form.frequency === "monthly" && (
            <MonthlyDayGrid value={form.daysOfMonth} onChange={v => setField("daysOfMonth", v)} />
          )}
          {form.frequency !== "hourly" && (
            <div className="flex flex-col gap-2.5">
              <SelectDropdown
                options={scheduleHourLabels}
                value={scheduleHourLabels[form.hourIndex]}
                onChange={v => setField("hourIndex", scheduleHourLabels.indexOf(v))}
                compact
                caret="triangle"
              />
            </div>
          )}
        </div>

        {/* What to send */}
        <div className="flex flex-col gap-3 rounded bg-main-background-secondary p-2">
          <SectionHeader icon={<DetailsIcon className="h-4 w-4 shrink-0" />} label="What to send" />
          <SegmentTabs
            options={SCOPE_OPTIONS}
            value={form.scope}
            onChange={v => setField("scope", v)}
            size="lg"
          />
          {blocks.length > 0 && (
            <div className={form.scope === "specific" ? undefined : "hidden"}>
              <BlockPicker
                blocks={blocks}
                selectedIds={form.blockIds}
                onAdd={id => setField("blockIds", [...form.blockIds, id])}
                onRemove={id =>
                  setField(
                    "blockIds",
                    form.blockIds.filter(x => x !== id)
                  )
                }
              />
            </div>
          )}
        </div>

        {/* Where to send */}
        <div className="flex min-h-0 flex-col gap-3 rounded bg-main-background-secondary p-2">
          <SectionHeader
            icon={<MessageIcon className="h-4 w-4 shrink-0" />}
            label="Where to send"
            action={
              form.destinations.length > 0 ? (
                <AddDestinationPicker
                  existingTypes={form.destinations.map(d => d.type)}
                  onAdd={type => handleAddDestination(type)}
                />
              ) : undefined
            }
          />
          {form.destinations.length === 0 ? (
            <div className="flex items-center gap-2">
              {DESTINATION_TYPE_OPTIONS_SUBSCRIBE_MODAL.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    if (opt.value === "slack" && !isSlackAuthorized) {
                      setAuthModalTarget("slack");
                      setPendingAuthDestinationType("slack");
                      return;
                    }
                    if (opt.value === "teams" && !isTeamsAuthorized) {
                      setAuthModalTarget("teams");
                      setPendingAuthDestinationType("teams");
                      return;
                    }
                    setField("destinations", [{ type: opt.value, recipients: [] }]);
                    if (opt.value === "slack" || opt.value === "teams") {
                      setAutoOpenDestinationIndex(0);
                    }
                  }}
                  className="flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg bg-main-text-button-hover-background/60 px-3 py-2 text-sm font-medium text-main-foreground transition-colors hover:bg-main-text-button-hover-background"
                >
                  <DestinationIcon type={opt.value} />
                  <span>{opt.label}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {form.destinations.map((dest, originalIdx) => {
                if (dest.type === "slack" && !isSlackAuthorized) {
                  return null;
                }
                if (dest.type === "teams" && !isTeamsAuthorized) {
                  return null;
                }
                return (
                  <DestinationBlock
                    key={`${dest.type}_${originalIdx}`}
                    destination={dest}
                    isSlackAuthorized={isSlackAuthorized}
                    isTeamsAuthorized={isTeamsAuthorized}
                    onReauthorizeSlack={onStartSlackOAuth}
                    onReauthorizeTeams={onStartTeamsOAuth}
                    autoOpenRecipients={autoOpenDestinationIndex === originalIdx}
                    onAutoOpenRecipientsHandled={() => setAutoOpenDestinationIndex(null)}
                    onChange={updated =>
                      setField(
                        "destinations",
                        form.destinations.map(d => (d === dest ? updated : d))
                      )
                    }
                    onRemove={() =>
                      setField(
                        "destinations",
                        form.destinations.filter(d => d !== dest)
                      )
                    }
                  />
                );
              })}
            </div>
          )}
          {formValidationError && (
            <div className="flex items-center gap-1.5 text-xs text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-lg">
              <span className="material-icons !text-sm">error_outline</span>
              {formValidationError}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-5 flex-shrink-0 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex h-8 items-center rounded bg-main-background-secondary px-3 text-sm font-semibold text-main-foreground transition-opacity hover:opacity-80 cursor-pointer"
            >
              Delete
            </button>
          )}
          {onPause && (
            <button
              type="button"
              onClick={onPause}
              className="inline-flex h-8 items-center rounded bg-main-background-secondary px-3 text-sm font-semibold text-main-foreground transition-opacity hover:opacity-80 cursor-pointer"
            >
              {isPaused ? "Resume Subscription" : "Pause Subscription"}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="h-8 px-3 text-sm font-semibold text-primary-button-foreground bg-primary-button-background rounded hover:bg-primary-button-hover-background disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer transition-colors flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <span className="material-icons !text-base animate-spin">refresh</span>
              Saving...
            </>
          ) : (
            "Save Subscription"
          )}
        </button>
      </div>

      <SlackSubscriptionAuthModal
        portalDocument={portalDocument}
        open={authModalTarget === "slack"}
        isAuthorizing={isSlackAuthInProgress}
        onAuthorize={() => onStartSlackOAuth?.()}
        onCancelAuthorize={onCancelSlackOAuth}
        onSkip={() => {
          onCancelSlackOAuth?.();
          setPendingAuthDestinationType(null);
          setAuthModalTarget(null);
        }}
      />

      <TeamsAuthModal
        portalDocument={portalDocument}
        open={authModalTarget === "teams"}
        isAuthorizing={isTeamsAuthInProgress}
        onAuthorize={onStartTeamsOAuth}
        onCancelAuthorize={onCancelTeamsOAuth}
        onSkip={() => {
          onCancelTeamsOAuth?.();
          setPendingAuthDestinationType(null);
          setAuthModalTarget(null);
        }}
      />
    </div>
  );
};
